/**
 * Yamiward - the Ward page engine (templates/page.ward.liquid, /pages/ward): the brand flash, the fit
 * finder, the Ward list confirmation and the Ward Board. layout/theme.liquid loads it on the Ward page
 * only. The product cards' behaviour moved to assets/cards.js in Task 3.2 (the homepage loads no lore
 * script); the transform carousel and the hero tilt card left with the old homepage.
 */

document.addEventListener("DOMContentLoaded", () => {

    // Elements
    const introScreen = document.getElementById("intro-screen");
    const storefrontWrapper = document.getElementById("storefront-wrapper");

    // Without the Ward page's markup there is nothing here to run. Leave quietly.
    if (!introScreen || !storefrontWrapper) return;

    // --- 1. BRAND FLASH ---
    // A first-impression moment, never a toll booth: no tap, no wait on storage
    // or network, about 600 ms. `is-intro` is NOT in the served HTML (a visitor
    // whose script never runs gets the store); it is added here and always
    // removed again in `finally`. Storage lives in try/catch: blocked storage
    // (private mode, strict settings) means "skip the flash", never "stay locked".
    const FLASH_MS = 600;
    const LATE_MS = 1500; // past this much page time, the store has been on screen: no flash

    function safeGet(key) {
        try { return localStorage.getItem(key); } catch (e) { return undefined; }
    }
    function safeSet(key, value) {
        try { localStorage.setItem(key, value); } catch (e) { /* storage blocked: the flash just plays again next visit */ }
    }

    function brandFlash() {
        const seen = safeGet("yw_entered"); // undefined = storage threw
        let reducedMotion = false;
        try { reducedMotion = window.matchMedia("(prefers-reduced-motion: reduce)").matches; } catch (e) { /* no matchMedia: play it */ }
        // Returning visitors, deep links / form submits, blocked storage and
        // reduced-motion visitors never get the flash. Nor does anyone who has
        // already been looking at the painted store (this script runs late) or a
        // background tab, where timers are throttled: a late flash would cover
        // a page they are already using.
        const late = document.hidden || performance.now() > LATE_MS;
        if (seen === undefined || seen === "1" || reducedMotion || late ||
            location.search.includes("customer_posted") || location.hash.length > 1) {
            if (seen === null) safeSet("yw_entered", "1");
            return;
        }

        const endFlash = () => {
            document.body.classList.remove("is-intro");
            safeSet("yw_entered", "1");
        };
        let timer = null;
        try {
            document.body.classList.add("is-intro");
            timer = setTimeout(endFlash, FLASH_MS);
        } finally {
            // if anything threw between add and scheduling, release the page now
            if (timer === null) endFlash();
        }
    }

    try { brandFlash(); } catch (e) { document.body.classList.remove("is-intro"); }

    // The header (menu, search, country) and the cart drawer are assets/shell.js's, on every page.

    // --- 4. INTERACTIVE SIZE FIT ENGINE ---
    const heightInput = document.getElementById("height-input");
    const weightInput = document.getElementById("weight-input");
    const heightVal = document.getElementById("height-val");
    const weightVal = document.getElementById("weight-val");
    const stylePref = document.getElementById("style-pref");
    const recommendedSize = document.getElementById("recommended-size");
    const fitVerdict = document.getElementById("fit-verdict");

    // Avatar Elements
    const avatarWireframe = document.getElementById("avatar-wireframe");
    const avatarTorso = document.getElementById("avatar-torso");
    const shirtOverlay = document.getElementById("shirt-overlay");

    function calculateFit() {
        const height = parseInt(heightInput.value, 10);
        const weight = parseInt(weightInput.value, 10);
        const style = stylePref.value;

        // Render input text values
        heightVal.textContent = height;
        weightVal.textContent = weight;

        // Sizing logic maps to streetwear standards
        let size = "M";
        let verdict = "";

        if (height < 165) {
            size = (weight < 60) ? "S" : "M";
        } else if (height >= 165 && height < 178) {
            if (weight < 65) size = "M";
            else if (weight >= 65 && weight < 80) size = "L";
            else size = "XL";
        } else if (height >= 178 && height < 188) {
            if (weight < 75) size = "L";
            else if (weight >= 75 && weight < 95) size = "XL";
            else size = "2XL";
        } else {
            size = (weight < 90) ? "XL" : "2XL";
        }

        // Adjust size recommendation based on styling preference
        if (style === "baggy") {
            if (size === "S") size = "M";
            else if (size === "M") size = "L";
            else if (size === "L") size = "XL";
            else if (size === "XL") size = "2XL";
        }

        // Output size
        recommendedSize.textContent = size;

        // Output copywriting fit advice
        if (style === "baggy") {
            verdict = `"${size} will give you an authentic oversized, drop-shoulder look. The hem lies mid-thigh and sleeves extend to the knuckles, matching lookbook styling."`;
        } else {
            verdict = `"${size} provides a classic standard streetwear profile. Loose around the chest but sitting standard on the waist, matching active casual wear."`;
        }
        fitVerdict.textContent = verdict;

        // Avatar Visual Scaling
        // Torso height maps to height (150 -> 80px, 200 -> 110px)
        const torsoHeight = 70 + ((height - 150) / 50) * 35;
        // Torso width maps to weight (45 -> 40px, 110 -> 75px)
        const torsoWidth = 40 + ((weight - 45) / 65) * 35;
        
        avatarTorso.style.height = `${torsoHeight}px`;
        avatarTorso.style.width = `${torsoWidth}px`;

        // Shirt Overlay scale based on fit preference
        const shirtWidthScale = (style === "baggy") ? 1.35 : 1.15;
        shirtOverlay.style.width = `${torsoWidth * shirtWidthScale}px`;
        shirtOverlay.style.height = `${torsoHeight * 0.95}px`;
    }

    // Event Listeners for Fit Builder
    if (heightInput && weightInput && stylePref) {
        heightInput.addEventListener("input", calculateFit);
        weightInput.addEventListener("input", calculateFit);
        stylePref.addEventListener("change", calculateFit);
        calculateFit(); // Run once initially
    }

    // --- 5. SHOPPING ---
    // The product cards are the homepage's (assets/cards.js); every cart request is assets/shell.js's.
    // The manga grid's "Shop the Drop" panel is a teaser: it sends people to the store's own grid.
    const addTeeBtn = document.getElementById("add-tee-btn");
    if (addTeeBtn) {
        addTeeBtn.addEventListener("click", () => {
            const root = document.body.getAttribute("data-root-url") || "/"; // locale-aware, printed by the layout
            window.location.href = root.replace(/\/+$/, "") + "/collections/all";
        });
    }

    // --- 6. SIGNUP FORM (real Shopify customer capture) ---
    // The form posts natively to Shopify ({% form 'customer' %} in the liquid);
    // after the redirect back, ?customer_posted=true shows the success state.
    const signupForm = document.getElementById("signup-form");
    const signupSuccess = document.getElementById("signup-success");
    if (signupSuccess && location.search.includes("customer_posted=true")) {
        if (signupForm) signupForm.style.display = "none";
        signupSuccess.style.display = "block";
        showToast("SOUL REGISTERED SUCCESSFULLY");
        signupSuccess.scrollIntoView({ behavior: "smooth", block: "center" });
    }

    // --- 7. WARD BOARD COMMUNITY FEEDS ENGINE (real backend) ---
    // Posts, comments and Spirit Energy live on the community API — shared by
    // every visitor, moderated, and rendered with textContent (never innerHTML
    // for user content) so nothing a user types can inject markup.
    const BOARD_API = "https://api.yamiward.com";
    const feedTimeline = document.getElementById("feed-timeline");
    const composerForm = document.getElementById("composer-form");

    // Identity for votes and the Daily Summon is issued and signed by the Ward
    // Board and attached by YW_ID.post/get_ — see identity.js.

    let posts = [];

    function relTime(iso) {
        const then = new Date(iso.replace(" ", "T") + (iso.includes("Z") ? "" : "Z")).getTime();
        const mins = Math.max(0, Math.floor((Date.now() - then) / 60000));
        if (mins < 1) return "just now";
        if (mins < 60) return `${mins} min${mins === 1 ? "" : "s"} ago`;
        const hrs = Math.floor(mins / 60);
        if (hrs < 24) return `${hrs} hour${hrs === 1 ? "" : "s"} ago`;
        const days = Math.floor(hrs / 24);
        return `${days} day${days === 1 ? "" : "s"} ago`;
    }

    async function loadPosts() {
        try {
            const res = await YW_ID.get_(`${BOARD_API}/api/posts`);
            posts = await res.json();
            renderFeed();
        } catch (e) {
            if (feedTimeline) feedTimeline.innerHTML = `<div class="cart-empty">THE BOARD IS UNREACHABLE — REFRESH TO RETRY.</div>`;
        }
    }

    // Render feed (DOM built with textContent for all user-supplied strings)
    function renderFeed() {
        if (!feedTimeline) return;
        feedTimeline.innerHTML = "";

        posts.forEach((post) => {
            const postCard = document.createElement("div");
            postCard.classList.add("feed-post");
            postCard.setAttribute("data-post-id", post.id);

            const header = document.createElement("div");
            header.className = "post-header";
            const meta = document.createElement("div");
            meta.className = "post-meta";
            const authorEl = document.createElement("span");
            authorEl.className = "post-author";
            authorEl.textContent = post.official ? `⛩ ${post.author}` : post.author;
            const clanEl = document.createElement("span");
            clanEl.className = "post-clan";
            clanEl.textContent = post.clan;
            const timeEl = document.createElement("span");
            timeEl.className = "post-time";
            timeEl.textContent = relTime(post.created_at);
            meta.appendChild(authorEl); meta.appendChild(clanEl); meta.appendChild(timeEl);
            const catEl = document.createElement("span");
            catEl.className = "post-category";
            catEl.textContent = "#" + post.category;
            header.appendChild(meta); header.appendChild(catEl);

            const titleEl = document.createElement("h3");
            titleEl.textContent = post.title;
            const bodyEl = document.createElement("div");
            bodyEl.className = "post-body";
            bodyEl.textContent = post.body;

            const actions = document.createElement("div");
            actions.className = "post-actions";
            const upvoteBtn = document.createElement("button");
            upvoteBtn.className = "action-btn-icon upvote-btn" + (post.liked ? " active" : "");
            upvoteBtn.innerHTML = `<span>🔥</span> <strong class="likes-count"></strong> Spirit Energy`;
            upvoteBtn.querySelector(".likes-count").textContent = post.likes;
            const commentToggleBtn = document.createElement("button");
            commentToggleBtn.className = "action-btn-icon comment-toggle-btn";
            commentToggleBtn.innerHTML = `<span>💬</span> <span class="c-count"></span> Comments`;
            commentToggleBtn.querySelector(".c-count").textContent = post.comments.length;
            actions.appendChild(upvoteBtn); actions.appendChild(commentToggleBtn);

            const commentSection = document.createElement("div");
            commentSection.className = "comment-section";
            const commentList = document.createElement("div");
            commentList.className = "comment-list";
            if (post.comments.length === 0) {
                const empty = document.createElement("div");
                empty.className = "comment-empty";
                empty.style.cssText = "font-size:0.75rem; color:var(--text-muted); font-family:var(--font-mono);";
                empty.textContent = "No scrolls written yet. Be the first.";
                commentList.appendChild(empty);
            } else {
                post.comments.forEach((comment) => {
                    const row = document.createElement("div");
                    row.className = "comment-row";
                    const cmeta = document.createElement("div");
                    cmeta.className = "comment-meta";
                    const ca = document.createElement("span");
                    ca.className = "comment-author";
                    ca.textContent = comment.author;
                    const ct = document.createElement("span");
                    ct.className = "comment-time";
                    ct.textContent = relTime(comment.created_at);
                    cmeta.appendChild(ca); cmeta.appendChild(ct);
                    const ctext = document.createElement("div");
                    ctext.className = "comment-text";
                    ctext.textContent = comment.body;
                    row.appendChild(cmeta); row.appendChild(ctext);
                    commentList.appendChild(row);
                });
            }
            const commentFormEl = document.createElement("form");
            commentFormEl.className = "comment-form";
            commentFormEl.innerHTML = `
                <input type="text" placeholder="Write a comment scroll..." class="comment-input signup-input" required maxlength="300">
                <button type="submit" class="btn btn-primary" style="padding:10px 20px; font-size:0.75rem;">Send</button>`;
            commentSection.appendChild(commentList);
            commentSection.appendChild(commentFormEl);

            const dot = document.createElement("span");
            dot.className = "halftone-dot";

            postCard.appendChild(header);
            postCard.appendChild(titleEl);
            postCard.appendChild(bodyEl);
            postCard.appendChild(actions);
            postCard.appendChild(commentSection);
            postCard.appendChild(dot);
            feedTimeline.appendChild(postCard);

            // --- Click Listeners for Post Actions ---

            // 1. Spirit Energy Upvote Toggle (server-side)
            upvoteBtn.addEventListener("click", async () => {
                try {
                    const res = await YW_ID.post(`${BOARD_API}/api/posts/${post.id}/vote`);
                    const out = await res.json();
                    if (!res.ok) { showToast((out.error || "VOTE FAILED").toUpperCase()); return; }
                    post.liked = out.liked;
                    post.likes = out.likes;
                    upvoteBtn.classList.toggle("active", out.liked);
                    upvoteBtn.querySelector(".likes-count").textContent = out.likes;
                    showToast(out.liked ? "SPIRIT ENERGY CHANNELED" : "SPIRIT ENERGY RECLAIMED");
                } catch { showToast("NETWORK ERROR"); }
            });
            commentToggleBtn.addEventListener("click", () => {
                commentSection.classList.toggle("active");
            });

            // 3. Comment Submission Form (server-side)
            commentFormEl.addEventListener("submit", async (e) => {
                e.preventDefault();
                const commentInput = commentFormEl.querySelector(".comment-input");
                const commentText = commentInput.value.trim();
                if (!commentText) return;
                const handle = safeGet("yw_handle") || "@realm_wanderer";
                try {
                    const res = await fetch(`${BOARD_API}/api/posts/${post.id}/comments`, {
                        method: "POST",
                        headers: { "content-type": "application/json" },
                        body: JSON.stringify({ author: handle, body: commentText })
                    });
                    const out = await res.json();
                    if (!res.ok) { showToast((out.error || "COMMENT FAILED").toUpperCase()); return; }
                    commentInput.value = "";
                    showToast(out.status === "pending" ? "SCROLL HELD FOR REVIEW" : "COMMENT SCROLL RELEASED");
                    await loadPosts();
                } catch { showToast("NETWORK ERROR"); }
            });

        });
    }

    // Handle Story Composer Submission (server-side)
    if (composerForm) {
        composerForm.addEventListener("submit", async (e) => {
            e.preventDefault();

            const usernameInput = document.getElementById("composer-username");
            const clanSelect = document.getElementById("composer-clan");
            const titleInput = document.getElementById("composer-title");
            const bodyInput = document.getElementById("composer-body");
            const categorySelect = document.getElementById("composer-category");

            let author = usernameInput.value.trim();
            if (!author.startsWith("@")) author = "@" + author;
            safeSet("yw_handle", author);

            try {
                const res = await fetch(`${BOARD_API}/api/posts`, {
                    method: "POST",
                    headers: { "content-type": "application/json" },
                    body: JSON.stringify({
                        author,
                        clan: clanSelect.value,
                        category: categorySelect.value,
                        title: titleInput.value.trim(),
                        body: bodyInput.value.trim()
                    })
                });
                const out = await res.json();
                if (!res.ok) { showToast((out.error || "STORY REJECTED").toUpperCase()); return; }

                titleInput.value = "";
                bodyInput.value = "";
                showToast(out.status === "pending" ? "STORY HELD FOR REVIEW" : "STORY CHANNELED TO THE BOARD");
                await loadPosts();
                feedTimeline.scrollIntoView({ behavior: "smooth" });
            } catch { showToast("NETWORK ERROR"); }
        });
    }

    // --- 7b. DAILY SUMMON ---
    const summonBtn = document.getElementById("summon-btn");
    const summonResult = document.getElementById("summon-result");
    if (summonBtn && summonResult) {
        summonBtn.addEventListener("click", async () => {
            summonBtn.disabled = true;
            summonBtn.textContent = "CHANNELING...";
            try {
                const res = await YW_ID.post(`${BOARD_API}/api/summon`, { hour: new Date().getHours() });
                const out = await res.json();
                if (!res.ok) { showToast((out.error || "THE GATE IS SEALED").toUpperCase()); return; }
                const r = out.reward;
                summonResult.innerHTML = "";
                const name = document.createElement("div");
                name.style.cssText = "font-family:var(--font-mono); color:#E51E25; font-weight:700; letter-spacing:0.1em; margin-bottom:6px;";
                const text = document.createElement("div");
                text.style.cssText = "font-size:0.85rem; color:var(--text-muted);";
                if (r.type === "code") {
                    name.textContent = "⭐ " + r.label;
                    text.textContent = "Apply the code at checkout. One summon per day — return tomorrow.";
                } else {
                    name.textContent = "🎴 " + r.name;
                    text.textContent = r.text + " (Collectible lore card. One summon per day — return tomorrow.)";
                }
                summonResult.appendChild(name);
                summonResult.appendChild(text);
                summonResult.style.display = "block";
                showToast(out.already ? "ALREADY SUMMONED TODAY" : (out.night ? "THE PARADE ANSWERS" : "THE GATE ANSWERS"));
            } catch { showToast("NETWORK ERROR"); }
            finally {
                summonBtn.disabled = false;
                summonBtn.textContent = "SUMMON";
            }
        });
    }

    // Initial load of the live board
    loadPosts();

    // --- 8. HELPER TOAST SYSTEM ---
    function showToast(message) {
        // Check if toast already exists
        let toast = document.querySelector(".toast-notification");
        if (!toast) {
            toast = document.createElement("div");
            toast.classList.add("toast-notification");
            document.body.appendChild(toast);
            
            // Add toast basic styling inline
            toast.style.position = "fixed";
            toast.style.bottom = "30px";
            toast.style.left = "50%";
            toast.style.transform = "translateX(-50%)";
            toast.style.backgroundColor = "#E51E25";
            toast.style.color = "#FFFFFF";
            toast.style.padding = "12px 24px";
            toast.style.fontFamily = "Space Mono, monospace";
            toast.style.fontSize = "0.75rem";
            toast.style.letterSpacing = "0.1em";
            toast.style.fontWeight = "700";
            toast.style.zIndex = "3000";
            toast.style.boxShadow = "0 10px 25px rgba(229, 30, 37, 0.4)";
            toast.style.transition = "opacity 0.3s ease, transform 0.3s ease";
            toast.style.borderRadius = "0px";
        }

        toast.textContent = message;
        toast.style.opacity = "1";
        toast.style.transform = "translateX(-50%) translateY(0)";

        setTimeout(() => {
            toast.style.opacity = "0";
            toast.style.transform = "translateX(-50%) translateY(10px)";
        }, 3000);
    }
});
